/**
 * Rebel Tide v0.6.3 — small add-ons loaded after rt.js (no dependencies).
 *
 *  - <rt-size-chart>: Size Chart link + size finder (height & weight → FIT / OVERSIZED).
 *  - <rt-upsell-card>: "Complete the look" cards in the bag (add with size).
 *  - Category photos inside the hamburger menu (moved in from a <template>); your own photos
 *    come from the RT · Menu categories section in the header.
 *  - Prev / next buttons for the swipe rows on desktop (menu photos, bag, pack builder photos).
 *  - Announcement bar hidden on the opening screen (home / collections), shown after it.
 *  - Home: the see-through menu stays on screen while the 001 OF 300 opening plays.
 *  - Home: a tap or click on the 001 OF 300 opening plays the scroll, so it never looks stuck.
 *  - Cards added later (infinite scroll, bag updates) are shown without waiting for a scroll reveal.
 */

const RT = (window.RT = window.RT || {});
const STORE_UNITS = 'rt-size-units';

/* ------------------------------------------------------------------ *
 * Helpers
 * ------------------------------------------------------------------ */

const esc = (value) => (window.CSS && CSS.escape ? CSS.escape(value) : String(value).replace(/["\\]/g, '\\$&'));
const norm = (value) => String(value || '').trim().toLowerCase();
// One size, several spellings: "2XL" in the chart can be "XXL" (or "TTG") in a translated store.
const sizeKey = (value) => {
  const v = norm(value).replace(/\s+/g, '');
  const xs = v.match(/^(x{2,6})l$/);
  if (xs) return `${xs[1].length}xl`;
  if (v === 'tg') return 'xl';
  if (v === 'ttg') return '2xl';
  return v;
};
const num = (value) => {
  const n = parseFloat(String(value || '').replace(',', '.'));
  return Number.isFinite(n) ? n : NaN;
};
const kgToLb = (kg) => Math.round(kg * 2.20462);
const cmToIn = (cm) => {
  const v = Math.round((cm / 2.54) * 10) / 10;
  return Number.isInteger(v) ? String(v) : v.toFixed(1);
};
const cmToFtIn = (cm) => {
  const total = Math.round(cm / 2.54);
  return `${Math.floor(total / 12)}'${total % 12}"`;
};
const splitRange = (text) =>
  String(text)
    .split(/\s*[-–]\s*/)
    .map(num)
    .filter((n) => Number.isFinite(n));

function readStore(key) {
  try {
    return window.localStorage.getItem(key);
  } catch (e) {
    return null;
  }
}

function writeStore(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch (e) {
    /* storage can be blocked: the choice just isn't remembered */
  }
}

/** Parse "Weight: 45, 50 …" + "160-165: S, M …" into weights and rows. */
function parseFitChart(text) {
  const lines = String(text || '')
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  const weights = [];
  const rows = [];
  lines.forEach((line, index) => {
    const [label, values = ''] = line.split(':');
    const cells = values.split(',').map((c) => c.trim());
    if (index === 0) {
      cells.forEach((c) => weights.push(num(c)));
      return;
    }
    const [lo, hi] = splitRange(label);
    if (!Number.isFinite(lo)) return;
    rows.push({ lo, hi: Number.isFinite(hi) ? hi : lo, sizes: cells.map((c) => (/^[-–·]?$/.test(c) ? '' : c)) });
  });
  rows.sort((a, b) => a.hi - b.hi);
  return { weights, rows };
}

/**
 * Chart lookup. Height: the row whose range contains it (upper bound included, like the
 * chart examples: 165 cm → 160-165). Weight: the nearest column (ties go to the heavier one).
 * Empty cell (tall and light): the first size listed for that height.
 */
function recommend(chart, order, cm, kg) {
  const { weights, rows } = chart;
  if (!rows.length || !weights.length) return null;

  let r = rows.findIndex((row) => cm <= row.hi);
  if (r === -1) r = rows.length - 1;

  let c = 0;
  let best = Infinity;
  weights.forEach((w, i) => {
    const d = Math.abs(kg - w);
    if (d < best - 1e-9 || (Math.abs(d - best) < 1e-9 && w > weights[c])) {
      best = d;
      c = i;
    }
  });

  const cells = rows[r].sizes;
  let col = c;
  let fit = cells[c];
  if (!fit) {
    for (let j = c + 1; j < cells.length && !fit; j += 1) if (cells[j]) [fit, col] = [cells[j], j];
    for (let j = c - 1; j >= 0 && !fit; j -= 1) if (cells[j]) [fit, col] = [cells[j], j];
  }
  if (!fit) return null;

  const i = order.findIndex((s) => norm(s) === norm(fit));
  const over = i >= 0 && i < order.length - 1 ? order[i + 1] : fit;
  const first = rows[0];
  const last = rows[rows.length - 1];
  const outside =
    cm < first.lo - 5 || cm > last.hi || kg < weights[0] - 5 || kg > weights[weights.length - 1] + 5;

  return { fit, over, largest: norm(over) === norm(fit), outside, row: r, col };
}

/* ------------------------------------------------------------------ *
 * <rt-size-chart>
 * ------------------------------------------------------------------ */

class RtSizeChart extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;

    this.dialog = this.querySelector('dialog');
    this.form = this.querySelector('[data-rt-sc-form]');
    this.errorEl = this.querySelector('[data-rt-sc-error]');
    this.resultEl = this.querySelector('[data-rt-sc-result]');
    this.noteEl = this.querySelector('[data-rt-sc-note]');
    try {
      this.config = JSON.parse(this.querySelector('[data-rt-sc-config]')?.textContent || '{}');
    } catch (e) {
      this.config = {};
    }
    this.text = this.config.text || {};
    this.chart = parseFitChart(this.config.fitChart);
    this.order = String(this.config.sizeOrder || 'XS, S, M, L, XL, 2XL, 3XL, 4XL, 5XL')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    this.prepareTables();

    this.querySelector('[data-rt-sc-open]')?.addEventListener('click', () => this.open());
    this.querySelector('[data-rt-sc-close]')?.addEventListener('click', () => this.close());
    this.dialog?.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.close();
    });
    this.dialog?.addEventListener('close', () => this.unlock());
    this.querySelectorAll('[data-rt-sc-unit]').forEach((button) =>
      button.addEventListener('click', () => this.setUnits(button.getAttribute('data-rt-sc-unit')))
    );
    this.form?.addEventListener('submit', (event) => {
      event.preventDefault();
      this.calculate();
    });
    this.form?.addEventListener('input', () => {
      if (this.errorEl) this.errorEl.hidden = true;
    });
    this.querySelectorAll('[data-pick]').forEach((button) =>
      button.addEventListener('click', () => this.pick(button.getAttribute('data-size') || ''))
    );

    const saved = readStore(STORE_UNITS);
    const country = window.Shopify && window.Shopify.country;
    const imperial = saved ? saved === 'imperial' : country === 'US' || (!country && /^en-us/i.test(navigator.language));
    this.setUnits(imperial ? 'imperial' : 'metric', { convert: false, remember: false });
  }

  open() {
    if (!this.dialog || this.dialog.open) return;
    if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
    else this.dialog.setAttribute('open', '');
    const root = document.documentElement;
    root.classList.add('rt-sc-lock');
    // Horizon's own scroll lock (also covers .page-wrapper, the scroller on large screens)
    if (!root.hasAttribute('scroll-lock')) {
      root.setAttribute('scroll-lock', '');
      this.lockedScroll = true;
    }
    const first = this.form?.querySelector(`[data-units="${this.units}"] input`);
    if (first && window.matchMedia('(hover: hover) and (pointer: fine)').matches) first.focus({ preventScroll: true });
  }

  close() {
    if (!this.dialog) return;
    if (typeof this.dialog.close === 'function') this.dialog.close();
    else this.dialog.removeAttribute('open');
    this.unlock();
  }

  unlock() {
    const root = document.documentElement;
    root.classList.remove('rt-sc-lock');
    if (this.lockedScroll) {
      root.removeAttribute('scroll-lock');
      this.lockedScroll = false;
    }
  }

  /** Keep the original (cm / kg) text of every table cell so switching units is lossless. */
  prepareTables() {
    const measure = this.querySelector('[data-rt-sc-measure]');
    if (measure) {
      const heads = [...measure.querySelectorAll('thead th')].map((th) => norm(th.textContent));
      this.measureCols = heads.map((h, i) => {
        if (i === 0) return 'label';
        if (/height|altura|taille|größe|grösse|alto|altezza|lichaamslengte|wzrost|kroppslängd|ύψος/.test(h)) return 'height';
        if (/weight|peso|poids|gewicht|waga|vikt|βάρος/.test(h)) return 'weight';
        return 'length';
      });
      measure.querySelectorAll('tbody tr').forEach((tr) => {
        [...tr.children].forEach((cell) => {
          cell.dataset.base = cell.textContent.trim();
        });
      });
      this.measure = measure;
    }
    this.fitTable = this.querySelector('[data-rt-sc-fitchart]');
  }

  setUnits(units, { convert = true, remember = true } = {}) {
    const next = units === 'imperial' ? 'imperial' : 'metric';
    if (convert && this.units && this.units !== next) this.convertInputs(next);
    this.units = next;
    if (remember) writeStore(STORE_UNITS, next);

    this.querySelectorAll('[data-rt-sc-unit]').forEach((button) =>
      button.setAttribute('aria-pressed', String(button.getAttribute('data-rt-sc-unit') === next))
    );
    this.querySelectorAll('[data-units]').forEach((group) => {
      group.hidden = group.getAttribute('data-units') !== next;
    });

    const unitLabel = this.querySelector('[data-rt-sc-table-unit]');
    if (unitLabel) unitLabel.textContent = next === 'imperial' ? '(in)' : '(cm)';

    if (this.measure) {
      this.measure.querySelectorAll('tbody tr').forEach((tr) => {
        [...tr.children].forEach((cell, i) => {
          const base = cell.dataset.base || '';
          const type = this.measureCols[i];
          if (next === 'metric' || type === 'label') {
            cell.textContent = base;
            return;
          }
          const values = splitRange(base);
          if (!values.length) {
            cell.textContent = base;
          } else if (type === 'height') {
            cell.textContent = values.map(cmToFtIn).join('–');
          } else if (type === 'weight') {
            cell.textContent = `${values.map(kgToLb).join('–')} lb`;
          } else {
            cell.textContent = values.map(cmToIn).join('–');
          }
        });
      });
    }

    if (this.fitTable) {
      this.fitTable.querySelectorAll('[data-kg]').forEach((th) => {
        const kg = num(th.getAttribute('data-kg'));
        th.textContent = next === 'imperial' ? `${kgToLb(kg)} lb` : `${th.getAttribute('data-kg')} kg`;
      });
      this.fitTable.querySelectorAll('[data-cm-range]').forEach((th) => {
        const range = th.getAttribute('data-cm-range') || '';
        th.textContent =
          next === 'imperial' ? splitRange(range).map(cmToFtIn).join('–') : `${range} cm`;
      });
    }
  }

  convertInputs(next) {
    const f = this.form;
    if (!f) return;
    const get = (name) => num(f.elements.namedItem(name)?.value);
    const set = (name, value) => {
      const input = f.elements.namedItem(name);
      if (input) input.value = Number.isFinite(value) ? String(value) : '';
    };
    if (next === 'imperial') {
      const cm = get('cm');
      const kg = get('kg');
      if (Number.isFinite(cm)) {
        const total = Math.round(cm / 2.54);
        set('ft', Math.floor(total / 12));
        set('in', total % 12);
      }
      if (Number.isFinite(kg)) set('lb', kgToLb(kg));
    } else {
      const ft = get('ft');
      const inch = get('in');
      const lb = get('lb');
      if (Number.isFinite(ft)) set('cm', Math.round((ft * 12 + (Number.isFinite(inch) ? inch : 0)) * 2.54));
      if (Number.isFinite(lb)) set('kg', Math.round((lb / 2.20462) * 10) / 10);
    }
  }

  readInputs() {
    const f = this.form;
    const get = (name) => num(f?.elements.namedItem(name)?.value);
    let cm;
    let kg;
    if (this.units === 'imperial') {
      const ft = get('ft');
      const inch = get('in');
      const lb = get('lb');
      if (!Number.isFinite(ft) || !Number.isFinite(lb)) return { error: this.text.empty };
      cm = (ft * 12 + (Number.isFinite(inch) ? inch : 0)) * 2.54;
      kg = lb / 2.20462;
    } else {
      cm = get('cm');
      kg = get('kg');
      if (!Number.isFinite(cm) || !Number.isFinite(kg)) return { error: this.text.empty };
    }
    if (cm < 120 || cm > 230 || kg < 30 || kg > 250) return { error: this.text.check };
    return { cm, kg };
  }

  calculate() {
    const input = this.readInputs();
    if (input.error) {
      if (this.errorEl) {
        this.errorEl.textContent = input.error || '';
        this.errorEl.hidden = false;
      }
      if (this.resultEl) this.resultEl.hidden = true;
      return;
    }

    const result = recommend(this.chart, this.order, input.cm, input.kg);
    if (!result || !this.resultEl) return;

    this.fill('fit', result.fit);
    this.fill('oversized', result.over);

    const notes = [];
    if (result.largest && this.text.largest) notes.push(this.text.largest.replace('[size]', result.fit));
    if (result.outside && this.text.outside) notes.push(this.text.outside);
    if (this.noteEl) {
      this.noteEl.textContent = notes.join(' ');
      this.noteEl.hidden = notes.length === 0;
    }

    this.resultEl.hidden = false;
    this.highlight(result);
    this.resultEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }

  fill(kind, size) {
    const box = this.querySelector(`[data-rt-sc-fit="${kind}"]`);
    if (!box) return;
    const sizeEl = box.querySelector('[data-size]');
    const statusEl = box.querySelector('[data-status]');
    const pickEl = box.querySelector('[data-pick]');
    if (sizeEl) sizeEl.textContent = size;

    const sizes = this.config.sizes || {};
    const key =
      Object.keys(sizes).find((k) => norm(k) === norm(size)) ??
      Object.keys(sizes).find((k) => sizeKey(k) === sizeKey(size));
    let status = '';
    let canPick = true;
    if (key === undefined) {
      status = this.text.notOffered || '';
      canPick = false;
    } else if (!sizes[key]) {
      status = this.text.soldOut || '';
      canPick = false;
    }
    if (statusEl) statusEl.textContent = status;
    if (pickEl) {
      pickEl.textContent = (this.text.select || 'Select [size]').replace('[size]', key || size);
      pickEl.setAttribute('data-size', key || size);
      pickEl.disabled = !canPick;
    }
  }

  highlight(result) {
    if (this.measure) {
      this.measure.querySelectorAll('tbody tr').forEach((tr) => {
        const label = norm(tr.firstElementChild?.dataset.base || tr.firstElementChild?.textContent);
        tr.classList.toggle('is-hit', label === norm(result.fit));
      });
    }
    if (this.fitTable) {
      this.fitTable.querySelectorAll('td.is-hit').forEach((td) => td.classList.remove('is-hit'));
      const row = this.fitTable.querySelectorAll('tbody tr')[result.row];
      const cell = row?.querySelectorAll('td')[result.col];
      cell?.classList.add('is-hit');
    }
  }

  pick(size) {
    if (!size) return;
    const scope = this.closest('.shopify-section') || document;
    const name = this.config.optionName || '';
    let input = name
      ? scope.querySelector(`variant-picker input[type="radio"][data-option-name="${esc(name)}"][value="${esc(size)}"]`)
      : null;
    if (!input) {
      input = [...scope.querySelectorAll('variant-picker input[type="radio"]')].find(
        (el) => norm(el.value) === norm(size) && (!name || norm(el.getAttribute('data-option-name')) === norm(name))
      );
    }
    if (input) {
      if (!input.checked) input.click();
    } else {
      const select = [...scope.querySelectorAll('variant-picker select')].find((el) =>
        [...el.options].some((o) => norm(o.value) === norm(size))
      );
      if (select) {
        const option = [...select.options].find((o) => norm(o.value) === norm(size));
        if (option) {
          select.value = option.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }
    this.close();
    const trigger = this.querySelector('[data-rt-sc-open]');
    if (trigger && this.text.selected) {
      trigger.setAttribute('data-rt-selected', this.text.selected.replace('[size]', size));
    }
  }
}

/* ------------------------------------------------------------------ *
 * <rt-upsell-card> — add a piece from the bag
 * ------------------------------------------------------------------ */

class RtUpsellCard extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-rt-ucard-add]');
    this.field = this.querySelector('[data-rt-ucard-variant]');
    this.errorEl = this.querySelector('[data-rt-ucard-error]');
    this.onAdd = this.onAdd.bind(this);
    this.button?.addEventListener('click', this.onAdd);
    if (this.field && this.field.tagName === 'SELECT') {
      this.field.addEventListener('change', () => {
        this.field.classList.remove('is-invalid');
        if (this.errorEl) this.errorEl.hidden = true;
      });
    }
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.onAdd);
  }

  async onAdd() {
    const id = this.field ? this.field.value : '';
    if (!id) {
      if (this.field) {
        this.field.classList.remove('is-invalid');
        void this.field.offsetWidth;
        this.field.classList.add('is-invalid');
        this.field.focus();
      }
      return;
    }
    if (typeof RT.addToCart !== 'function' || !this.button) return;
    this.button.setAttribute('aria-busy', 'true');
    if (this.errorEl) this.errorEl.hidden = true;
    try {
      await RT.addToCart([{ id, quantity: 1 }], this, 'cart');
    } catch (error) {
      if (this.errorEl) {
        this.errorEl.textContent = (error && error.message) || '';
        this.errorEl.hidden = !this.errorEl.textContent;
      }
    } finally {
      this.button?.removeAttribute('aria-busy');
    }
  }
}

/* ------------------------------------------------------------------ *
 * Swipe rows (menu categories, bag upsell, pack builder photos):
 * prev / next buttons. Clicks and scrolls are delegated, so rows that the
 * theme re-renders (header hydration, bag updates, pack switch) keep
 * working without re-binding.
 * ------------------------------------------------------------------ */

const ROW_ROOT = '[data-rt-dcats], [data-rt-upsell], [data-rt-row]';
const ROW_LIST = '[data-rt-dcats-list], [data-rt-upsell-list], [data-rt-row-list]';
const ROW_PREV = '[data-rt-dcats-prev], [data-rt-upsell-prev], [data-rt-row-prev]';
const ROW_NEXT = '[data-rt-dcats-next], [data-rt-upsell-next], [data-rt-row-next]';

function rowParts(el) {
  const root = el instanceof Element ? el.closest(ROW_ROOT) : null;
  if (!root) return null;
  return {
    list: root.querySelector(ROW_LIST),
    prev: root.querySelector(ROW_PREV),
    next: root.querySelector(ROW_NEXT),
  };
}

function updateRow(parts) {
  if (!parts || !parts.list) return;
  const { list, prev, next } = parts;
  const max = list.scrollWidth - list.clientWidth - 4;
  if (prev) prev.disabled = list.scrollLeft <= 4;
  if (next) next.disabled = max <= 0 || list.scrollLeft >= max;
}

// Rows hidden in a closed drawer measure 0: re-check them when they get a size.
const rowSizes =
  typeof ResizeObserver === 'function'
    ? new ResizeObserver((entries) => entries.forEach((entry) => updateRow(rowParts(entry.target))))
    : null;
const watchedLists = new WeakSet();

function bindRows(scope = document) {
  scope.querySelectorAll(ROW_LIST).forEach((list) => {
    if (watchedLists.has(list)) return;
    watchedLists.add(list);
    rowSizes?.observe(list);
    updateRow(rowParts(list));
  });
}

document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest(`${ROW_PREV}, ${ROW_NEXT}`) : null;
  if (!button) return;
  const parts = rowParts(button);
  if (!parts || !parts.list) return;
  const { list } = parts;
  const item = list.firstElementChild;
  const width = item ? item.getBoundingClientRect().width + 10 : list.clientWidth * 0.8;
  const dir = button.matches(ROW_PREV) ? -1 : 1;
  list.scrollBy({ left: dir * Math.max(width, list.clientWidth * 0.66), behavior: 'smooth' });
});

document.addEventListener(
  'scroll',
  (event) => {
    if (event.target instanceof Element && event.target.matches(ROW_LIST)) updateRow(rowParts(event.target));
  },
  { capture: true, passive: true }
);

/* ------------------------------------------------------------------ *
 * Pack builder: minimalist arrows on each side of the photo row, for
 * mouse and trackpad (fingers swipe). rt.js builds each picker; its photo
 * row is wrapped here right after, every time a pack is switched.
 * ------------------------------------------------------------------ */

const ARROW_ICONS = {
  prev: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M10 3.5 5.5 8l4.5 4.5"/></svg>',
  next: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M6 3.5 10.5 8 6 12.5"/></svg>',
};

function arrowButton(dir) {
  const strings = (window.RT && window.RT.config && window.RT.config.strings) || {};
  const button = document.createElement('button');
  button.type = 'button';
  button.className = `rt-row-arrow rt-row-arrow--${dir}`;
  button.setAttribute(dir === 'prev' ? 'data-rt-row-prev' : 'data-rt-row-next', '');
  button.setAttribute('aria-label', dir === 'prev' ? strings.previous || 'Previous' : strings.next || 'Next');
  button.innerHTML = ARROW_ICONS[dir];
  if (dir === 'prev') button.disabled = true;
  return button;
}

function decoratePickers(scope = document) {
  scope.querySelectorAll('.rt-picker__models').forEach((list) => {
    if (list.closest('[data-rt-row]')) return;
    const row = document.createElement('div');
    row.className = 'rt-picker__row';
    row.setAttribute('data-rt-row', '');
    list.setAttribute('data-rt-row-list', '');
    list.before(row);
    row.append(list, arrowButton('prev'), arrowButton('next'));
  });
}

/* ------------------------------------------------------------------ *
 * Category photos in the hamburger menu
 * The row is rendered in a <template> and placed right after the menu
 * links. The RT · Menu categories section (header, one block per category
 * with its own photo) wins over the row from the theme settings (rt-head).
 * The theme refreshes the header once after the page loads (section
 * hydration), which drops what it didn't render, so the row is put back
 * whenever the drawer loses it.
 * ------------------------------------------------------------------ */

function categoriesTemplate() {
  const own = document.getElementById('rt-dcats-section-template');
  if (own instanceof HTMLTemplateElement && own.content.querySelector('[data-rt-dcats]')) return own;
  const base = document.getElementById('rt-dcats-template');
  return base instanceof HTMLTemplateElement ? base : null;
}

/** @param {boolean} [fresh] - rebuild the row (theme editor changes) instead of keeping it */
function mountDrawerCategories(fresh = false) {
  const template = categoriesTemplate();
  document.querySelectorAll('.menu-drawer').forEach((drawer) => {
    const nav = drawer.querySelector('.menu-drawer__navigation');
    if (!nav) return;
    const current = drawer.querySelector('[data-rt-dcats]');
    if (current && !fresh) {
      if (nav.nextElementSibling !== current) nav.after(current);
      return;
    }
    current?.remove();
    if (template) nav.after(template.content.cloneNode(true));
  });
  bindRows(document);
}

function touchesDrawer(mutation) {
  const target = mutation.target;
  if (target instanceof Element && target.closest('.menu-drawer, .header__drawer')) return true;
  for (const node of mutation.addedNodes) {
    if (node instanceof Element && (node.matches('.menu-drawer') || node.querySelector('.menu-drawer'))) return true;
  }
  return false;
}

/* ------------------------------------------------------------------ *
 * Opening screen (the 001 OF 300 intro, the collection banner)
 * - Announcement bar only after it: rt-head adds .rt-ann-scroll and
 *   .rt-scrolled is set once the first section has been scrolled past.
 * - Home intro: the see-through menu with the logo stays on screen while
 *   the scene plays (.rt-intro-pin), then leaves with the intro's last edge.
 * ------------------------------------------------------------------ */

let pinState = '';

function pinHeaderOverIntro(first, bottom) {
  const root = document.documentElement;
  const header = document.getElementById('header-component');
  let pin = false;
  let offset = 0;
  if (header && header.hasAttribute('transparent') && first && first.querySelector('rt-intro')) {
    const height = header.offsetHeight || 60;
    if (bottom >= height) {
      pin = true;
    } else if (bottom > 0 && header.dataset.stickyState !== 'active') {
      // The intro is leaving the screen: the menu goes with its bottom edge.
      pin = true;
      offset = Math.round(bottom - height);
    }
  }
  const next = pin ? `pin:${offset}` : 'off';
  if (next === pinState) return;
  pinState = next;
  root.classList.toggle('rt-intro-pin', pin);
  root.style.setProperty('--rt-pin-y', `${offset}px`);
}

function watchOpeningScreen() {
  const root = document.documentElement;
  const annScroll = root.classList.contains('rt-ann-scroll');
  let frame = null;
  const check = () => {
    frame = null;
    const first = document.querySelector('.content-for-layout > .shopify-section:first-child');
    const bottom = first ? first.getBoundingClientRect().bottom : 0;
    if (annScroll) root.classList.toggle('rt-scrolled', bottom <= 1);
    pinHeaderOverIntro(first, bottom);
  };
  const schedule = () => {
    if (frame === null) frame = requestAnimationFrame(check);
  };
  document.addEventListener('scroll', schedule, { capture: true, passive: true });
  window.addEventListener('resize', schedule, { passive: true });
  document.addEventListener('shopify:section:load', schedule);
  document.addEventListener('shopify:section:reorder', schedule);
  check();
}

/* ------------------------------------------------------------------ *
 * Opening screen: a tap or a click plays the scroll
 * The 001 OF 300 scene opens as the page scrolls. A tap (phone) or a click
 * (desktop) anywhere on it scrolls for the visitor, so the same animation
 * plays and the screen never looks stuck: first to the open photo, then on
 * to what comes next. Links and buttons work as usual, and any wheel,
 * touch or key from the visitor takes over at once.
 * ------------------------------------------------------------------ */

const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const TAKEOVER = ['wheel', 'touchstart', 'pointerdown', 'keydown'];
const tapIntros = new WeakSet();
let glide = null;

/** What scrolls the page: Horizon's .page-wrapper on large screens, the window on phones. */
function pageScroller(el) {
  for (let node = el.parentElement; node && node !== document.body; node = node.parentElement) {
    const overflowY = getComputedStyle(node).overflowY;
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight + 1) return node;
  }
  return document.scrollingElement || document.documentElement;
}

function glideBy(scroller, distance, duration) {
  glide?.stop();
  const from = scroller.scrollTop;
  const started = performance.now();
  const behavior = scroller.style.scrollBehavior;
  let frame = 0;
  const stop = () => {
    cancelAnimationFrame(frame);
    scroller.style.scrollBehavior = behavior;
    TAKEOVER.forEach((type) => window.removeEventListener(type, stop, true));
    if (glide && glide.stop === stop) glide = null;
  };
  const step = (now) => {
    const t = Math.min(1, (now - started) / duration);
    scroller.scrollTop = from + distance * easeInOutCubic(t);
    if (t < 1) frame = requestAnimationFrame(step);
    else stop();
  };
  scroller.style.scrollBehavior = 'auto';
  TAKEOVER.forEach((type) => window.addEventListener(type, stop, { capture: true, passive: true }));
  glide = { stop };
  frame = requestAnimationFrame(step);
}

/** @param {MouseEvent} event */
function onIntroTap(event) {
  const intro = event.currentTarget;
  const root = document.documentElement;
  if (!(intro instanceof HTMLElement) || event.defaultPrevented || event.button !== 0) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  if (intro.classList.contains('is-static') || intro.classList.contains('is-auto')) return;
  if (root.getAttribute('data-rt-intro') === 'done' || root.getAttribute('data-rt-motion') === 'off') return;
  const target = event.target instanceof Element ? event.target : null;
  if (target && target.closest('a, button, input, select, textarea, label, summary, [role="button"]')) return;
  const stage = intro.querySelector('.rt-intro__stage');
  const rect = intro.getBoundingClientRect();
  const screen = (stage instanceof HTMLElement && stage.clientHeight) || window.innerHeight;
  const travel = rect.height - screen;
  if (travel <= 0) return;
  const progress = Math.min(1, Math.max(0, -rect.top / travel));
  // First tap: to the fully open photo. Once it is open: on to the next section.
  const distance = progress < 0.9 ? travel + rect.top : rect.bottom;
  if (distance < 2) return;
  const lite = root.getAttribute('data-rt-motion') === 'lite';
  const duration = lite ? 650 : Math.round(Math.min(1700, Math.max(900, (distance / screen) * 1100)));
  glideBy(pageScroller(intro), distance, duration);
}

function bindIntroTap(scope = document) {
  scope.querySelectorAll('rt-intro').forEach((intro) => {
    if (tapIntros.has(intro)) return;
    tapIntros.add(intro);
    // Bound on the element itself: iPhones only send taps on plain areas to elements that listen.
    intro.addEventListener('click', onIntroTap);
  });
}

// Theme editor: keep the bar visible while it (or one of its announcements) is selected.
const isAnnouncement = (target) =>
  target instanceof Element && Boolean(target.closest('.announcement-bar') || target.querySelector('.announcement-bar'));
['shopify:section:select', 'shopify:block:select'].forEach((type) =>
  document.addEventListener(type, (event) => {
    if (isAnnouncement(event.target)) document.documentElement.classList.add('rt-ann-editing');
  })
);
['shopify:section:deselect', 'shopify:block:deselect'].forEach((type) =>
  document.addEventListener(type, (event) => {
    if (isAnnouncement(event.target)) document.documentElement.classList.remove('rt-ann-editing');
  })
);

/* ------------------------------------------------------------------ *
 * Content added later (infinite scroll, bag re-render): show it right away
 * ------------------------------------------------------------------ */

function revealAdded(node) {
  if (!(node instanceof Element)) return;
  if (node.matches('[data-rt-reveal]:not(.is-in)')) node.classList.add('is-in');
  node.querySelectorAll('[data-rt-reveal]:not(.is-in)').forEach((el) => el.classList.add('is-in'));
  if (node.matches('.rt-picker__models') || node.querySelector('.rt-picker__models')) decoratePickers(node.parentElement || document);
  if (node.matches(ROW_LIST) || node.querySelector(ROW_LIST)) bindRows(node.parentElement || document);
}

/* ------------------------------------------------------------------ *
 * Boot
 * ------------------------------------------------------------------ */

if (!customElements.get('rt-size-chart')) customElements.define('rt-size-chart', RtSizeChart);
if (!customElements.get('rt-upsell-card')) customElements.define('rt-upsell-card', RtUpsellCard);

const boot = () => {
  watchOpeningScreen();
  bindIntroTap(document);
  mountDrawerCategories();
  decoratePickers(document);
  bindRows(document);
  new MutationObserver((mutations) => {
    let drawerChanged = false;
    for (const mutation of mutations) {
      mutation.addedNodes.forEach(revealAdded);
      if (!drawerChanged && touchesDrawer(mutation)) drawerChanged = true;
    }
    if (drawerChanged) mountDrawerCategories();
  }).observe(document.body, { childList: true, subtree: true });
};

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();

document.addEventListener('shopify:section:load', () => {
  mountDrawerCategories(true);
  decoratePickers(document);
  bindRows(document);
  bindIntroTap(document);
});

// Theme editor: RT · Menu categories removed → the row goes back to the theme settings one.
document.addEventListener('shopify:section:unload', (event) => {
  const own = event.target instanceof Element ? event.target.querySelector('#rt-dcats-section-template') : null;
  if (!own) return;
  own.removeAttribute('id');
  mountDrawerCategories(true);
});

export { parseFitChart, recommend };